import { useEffect, useState } from "react";

import { fetchOptions, fetchPacks } from "services/contract.services";
import { differenceInDays, differenceInWeeks } from "date-fns";
import { Checkbox, MessageBox, PrevNextButton } from "reusableComponents";
import { selectAuthToken } from "store/auth/selectors";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLanguage } from "context";
import {
  fetchSpaceDataWithAuth,
  submitStep4,
} from "services/addSpace.services";

import styles from "./styles.module.scss";

const Step4 = () => {
  const [options, setOptions] = useState([]);
  const [packs, setPacks] = useState([]);
  const [spaceData, setSpaceData] = useState(null);
  const [externalRate, setExternalRate] = useState(0);
  const [baseRate] = useState(10);
  const [optionsRate, setOptionsRate] = useState(0);
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { language } = useLanguage();
  const accessToken = useSelector(selectAuthToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const calculateRate = () => {
    let rate = 0;
    options.forEach((option) => {
      if (option.checked && !option.disabled && !option.fromPack) {
        rate += option.percentage || 0;
      }
    });
    packs.forEach((pack) => {
      if (pack.checked) {
        rate += pack.percentage || 0;
      }
    });

    // Use functional update to ensure state is updated correctly
    setOptionsRate((prevRate) => rate);
  };

  const processOptions = (placeOptions, selectedOptions, selectedPacks) => {
    // Extract all option IDs from selected packs
    const packOptionsMap = new Map();
    selectedPacks.forEach((pack) => {
      pack.pack.options.forEach((option) => {
        packOptionsMap.set(option.id, pack.pack); // Map option ID to its pack
      });
    });

    return placeOptions.map((option) => {
      const isOptionInSelected = selectedOptions.some(
        (opt) => opt.option.id === option.id
      );
      const fromPack = packOptionsMap.get(option.id); // Get pack if option is inside one

      return {
        ...option,
        checked: isOptionInSelected || Boolean(fromPack), // Mark as checked if selected or in a pack
        fromPack, // Store the pack if applicable
        disabled: Boolean(fromPack), // Disable if inside a pack
        uncheckable: !isOptionInSelected && !fromPack, // Can only be unchecked if not pre-selected or in a pack
        weeks:
          option.duration % 7 === 0
            ? differenceInWeeks(
                new Date(),
                new Date(
                  new Date().setDate(new Date().getDate() - option.duration)
                )
              )
            : null,
        days:
          option.duration % 7 !== 0
            ? differenceInDays(
                new Date(),
                new Date(
                  new Date().setDate(new Date().getDate() - option.duration)
                )
              )
            : null,
      };
    });
  };

  const processPacks = (placePacks, selectedPacks) => {
    return placePacks.map((pack) => ({
      ...pack,
      checked: selectedPacks.some((p) => p.pack.id === pack.id),
      weeks:
        pack.duration % 7 === 0
          ? differenceInWeeks(
              new Date(),
              new Date(new Date().setDate(new Date().getDate() - pack.duration))
            )
          : null,
      days:
        pack.duration % 7 !== 0
          ? differenceInDays(
              new Date(),
              new Date(new Date().setDate(new Date().getDate() - pack.duration))
            )
          : null,
      uncheckable: !selectedPacks.some((p) => p.pack.id === pack.id),
    }));
  };

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
     const [optionsData, packsData, spaceDataResponse] = await Promise.all([
  fetchOptions(accessToken, language),
  fetchPacks(accessToken, language),
  fetchSpaceDataWithAuth(spaceId, accessToken, language),
]);

      // Set default instant_booking to false if not already set
      const processedSpaceData = {
        ...spaceDataResponse,
        instant_booking: spaceDataResponse?.instant_booking ?? false, // Default to false if undefined
      };

      setOptions(
        processOptions(
          optionsData,
          processedSpaceData.options,
          processedSpaceData.packs
        ) || []
      );
      setPacks(processPacks(packsData, processedSpaceData.packs) || []);
      setSpaceData(processedSpaceData || null);
      setExternalRate(spaceData?.external_commission || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [spaceId, accessToken, language]);

  const switchOption = (selectedOption) => {
    setOptions((prevOptions) =>
      prevOptions.map((opt) =>
        opt.id === selectedOption.id ? { ...opt, checked: !opt.checked } : opt
      )
    );

    // Get updated selected options list
    // const selectedOptions = options
    //   .map((opt) => (opt.id === selectedOption.id ? { ...opt, checked: !opt.checked } : opt))
    //   .filter((opt) => opt.checked)
    //   .map((opt) => opt.id);

    // // Find currently selected pack
    // const selectedPack = packs.find((pack) => pack.checked) || null;

    // // Find a new pack that has all its options checked
    // const newPack = packs.find(
    //   (pack) => !pack.checked && pack.options.every((opt) => selectedOptions.includes(opt.id))
    // );

    // // Switch pack if necessary
    // if (!selectedPack && newPack) {
    //   switchPack(newPack);
    // }

    // Recalculate the rate
    calculateRate();
  };

  const switchPack = (pack) => {
    // Check if the selected pack is already checked (i.e., deselect it if so)
    const isPackAlreadySelected = packs.some(
      (p) => p.id === pack.id && p.checked
    );

    if (isPackAlreadySelected) {
      // If the pack is already selected, deselect it and uncheck only the options from this pack
      setPacks((prevPacks) =>
        prevPacks.map((p) => (p.id === pack.id ? { ...p, checked: false } : p))
      );

      setOptions((prevOptions) =>
        prevOptions.map((opt) => {
          // Uncheck options only that belong to the deselected pack
          const isOptionInPack = pack.options.some((po) => po.id === opt.id);
          return {
            ...opt,
            checked: isOptionInPack ? false : opt.checked, // Uncheck only options from this pack
            fromPack: isOptionInPack ? null : opt.fromPack, // Reset the `fromPack` property for these options
            disabled: isOptionInPack ? false : opt.disabled, // Re-enable options that were disabled by this pack
          };
        })
      );
    } else {
      // If the pack is not already selected, switch the pack as usual
      const previouslyCheckedOptions = options
        .filter((opt) => opt.checked)
        .map((opt) => opt.id);

      setPacks((prevPacks) =>
        prevPacks.map((p) =>
          p.id === pack.id ? { ...p, checked: true } : { ...p, checked: false }
        )
      );

      setOptions((prevOptions) =>
        prevOptions.map((opt) => {
          const isOptionInPack = pack.options.some((po) => po.id === opt.id);
          return {
            ...opt,
            checked:
              previouslyCheckedOptions.includes(opt.id) || isOptionInPack,
            disabled: isOptionInPack,
            fromPack: isOptionInPack ? pack : null,
          };
        })
      );
    }

    calculateRate();
  };
  const handleCheckboxChange = (value) => {
    setSpaceData((prev) => ({
      ...prev,
      instant_booking: value,
    }));
  };

  const handleSubmit = async (direction) => {
    setLoading(true);
    if (direction === "prev") {
      navigate(`/create-space/step3/${spaceId}`);
      setLoading(false);
      return;
    }
    try {
      const payload = {
        ...spaceData,
        place_id: spaceId,
        options: options
          .filter((option) => option.checked)
          .map((option) => option.id),
        packs: packs.filter((pack) => pack.checked).map((pack) => pack.id),
      };

      // Mock API call to submit step 4
      const response = await submitStep4(
        payload,
        spaceId,
        language,
        accessToken
      );

      if (direction === "next") {
        navigate(`/create-space/confirmation/${spaceId}`);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calculateRate();
  }, [options, packs]);

  if (error) return <p>Error: {error}</p>;

  return (
    <div className={`${styles.step4} ${loading ? styles.loading : ""}`}>
      <PrevNextButton
        color="nav"
        onNext={() => handleSubmit("next")}
        onPrev={() => handleSubmit("prev")}
        isIconOnly
      />
      <div className={styles.instantBookingQuery}>
        <h4 className={styles.queryTitle}>
          {t("provide.step4.instantBooking.title")}
        </h4>
        <p className={styles.queryText}>
          {t("provide.step4.instantBooking.description")}
        </p>
        <Checkbox
          isSquare
          checked={spaceData?.instant_booking === true}
          onChange={() => handleCheckboxChange(true)}
          label={
            <div className={styles.checkboxLabel}>
              <span>{t("provide.step4.instantBooking.yes")}</span>
              <p>{t("provide.step4.instantBooking.yesDescription")}</p>
            </div>
          }
        />
        <Checkbox
          isSquare
          checked={spaceData?.instant_booking === false}
          onChange={() => handleCheckboxChange(false)}
          label={
            <div className={styles.checkboxLabel}>
              <span>{t("provide.step4.instantBooking.no")}</span>
              <p>{t("provide.step4.instantBooking.noDescription")}</p>
            </div>
          }
        />
      </div>
      <div className={styles.body}>
        <div className={styles.options}>
          <h4 className={styles.subtitle}>{t("provide.step4.title")}</h4>
          <div className={styles.itemsList}>
            {options.map((option) => (
              <div
                key={option.id}
                className={`${styles.item} ${
                  option.disabled ? styles.disabled : ""
                }`}
              >
                <img
                  alt="option"
                  className={styles.image}
                  src={option.image_url.replace(/\\\//g, "/")}
                />
                <Checkbox
                  iconType
                  isSquare
                  checked={option.checked}
                  disabled={option.disabled}
                  onChange={() => switchOption(option)}
                />
                <div className={styles.content}>
                  <h4
                    className={styles.name}
                    style={{ color: `${option.color}` }}
                  >
                    {option.name}
                  </h4>
                  <span className={styles.description}>
                    {option.description}
                  </span>
                  {option.id !== "67836b9b-6762-41f3-a180-91326560e51b"? (
                    <span className={styles.percentage}>
                      {`${t("provide.step4.options.commissionRate", {
                        value: option.percentage,
                      })} ${t("provide.step4.options.weeks", {
                        weeks: option.duration / 7,
                      })}`}
                    </span>
                  ): (
                    <span className={styles.percentage}>
                      {`${t("provide.step4.options.commissionRate", {
                        value: option.percentage,
                      })} ${t("provide.step4.options.days", {
                        days: option.duration,
                      })}`}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.packs}>
          <h3 className={styles.subtitle}>{t("provide.step4.packs.title")}</h3>
          <div className={styles.itemsList}>
            {packs.map((pack) => (
              <div
                key={pack.id}
                className={`${styles.item} ${
                  pack.disabled ? styles.disabled : ""
                }`}
              >
                <img
                  alt="option"
                  className={styles.image}
                  src={pack.image_url.replace(/\\\//g, "/")}
                />
                <Checkbox
                  iconType
                  isSquare
                  checked={pack.checked}
                  disabled={pack.disabled}
                  onChange={() => switchPack(pack)}
                />
                <div className={styles.content}>
                  <h4
                    className={styles.name}
                    style={{ color: `${pack.color}` }}
                  >
                    {pack.name}
                  </h4>
                  <span className={styles.description}>{pack.description}</span>
                  <span className={styles.percentage}>
                    {`${t("provide.step4.options.commissionRate", {
                      value: pack.percentage,
                    })} ${t("provide.step4.options.weeks", {
                      weeks: pack.duration / 7,
                    })}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.commissions}>
        <span className={styles.commission}>
          {t("provide.step4.commissions.base", {
            rate: baseRate,
          })}
        </span>
        <span className={styles.commission}>
          {t("provide.step4.commissions.options", {
            rate: optionsRate,
          })}
        </span>
        <h4 className={styles.commissionTotal}>
          {t("provide.step4.commissions.total", {
            rate: baseRate + optionsRate,
          })}
        </h4>
        <span className={styles.percentage}>
          {" "}
          {t("provide.step4.commissions.taxes")}
        </span>
      </div>
      <div className={styles.nextBtnContainer}>
        <PrevNextButton
          color="nav"
          onNext={() => handleSubmit("next")}
          onPrev={() => handleSubmit("prev")}
        />
      </div>{" "}
    </div>
  );
};

export default Step4;
